"use client";

import React from "react";
import type {MonthRating, Festival} from "@/lib/content-parser";
import {renderTextWithLinks} from "@/lib/render-markdown-links";
import {Calendar, CalendarCheck2, Check} from "lucide-react";
import {Card, CardHeader, CardTitle, CardAction, CardContent} from "@/components/ui/card";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger} from "@/components/ui/dropdown-menu";
import {getPreferenceColour} from "./preference-colours";
import {getFestivalAnchorId} from "./FestivalList";


interface Preference {
    id: string;
    label: string;
}

interface PreferenceMonthGridProps {
    months: MonthRating[];
    preferences: Preference[];
    selectedIds: Set<string>;
    festivals?: Festival[];
    festivalHeading?: string;
}

const MAX_RATING = 5;


function RatingBar({rating, colour, max = MAX_RATING}: {rating: number; colour?: string; max?: number}) {
    const pct = (rating / max) * 100;
    return (
        <span role="img" className="inline-block h-1.5 w-16 overflow-hidden rounded-full border" aria-label={`${rating} out of ${max}`} style={{borderColor: `oklch(from ${colour ?? "var(--color-primary)"} l c h / 0.7)`, backgroundColor: `oklch(from ${colour ?? "var(--color-primary)"} l c h / 0.1)`}}>
            <span
                className="block h-full rounded-full"
                style={{width: `${pct}%`, backgroundColor: `oklch(from ${colour ?? "var(--color-primary)"} l c h / 0.7)`}}
            />
        </span>
    );
}

export function PreferenceMonthGrid({months, preferences, selectedIds, festivals, festivalHeading}: PreferenceMonthGridProps) {
    const hasSelection = selectedIds.size > 0;

    // Group festivals by month (excluding "Varies")
    const festivalsByMonth = new Map<string, Festival[]>();
    for (const f of festivals ?? []) {
        if (f.month === "Varies") continue;
        const list = festivalsByMonth.get(f.month) ?? [];
        list.push(f);
        festivalsByMonth.set(f.month, list);
    }

    // Build lookups from preference id to short label and colour
    const prefLabelMap = new Map(preferences.map((p) => [p.id, p.label]));
    const prefColourMap = new Map(preferences.map((p, i) => [p.id, getPreferenceColour(i)]));

    // Calculate scores per month
    const scores = months.map((month) => {
        if (!hasSelection) return 0;
        return month.sectionRatings
            .filter((r) => selectedIds.has(r.sectionId))
            .reduce((sum, r) => sum + r.rating, 0);
    });

    const maxScore = Math.max(...scores);

    // Normalize against theoretical max (5 per selected preference) so scores
    // don't all converge when multiple preferences are selected
    const theoreticalMax = selectedIds.size * MAX_RATING;

    // Linear normalization against theoretical max, then min-max rescale
    // so the best month hits 1.0 and the worst hits 0.0
    const rawIntensities = scores.map((score) => {
        if (!hasSelection || theoreticalMax === 0) return 0;
        return score / theoreticalMax;
    });
    const peakIntensity = Math.max(...rawIntensities);
    const minIntensity = Math.min(...rawIntensities);
    const range = peakIntensity - minIntensity;
    const intensities = rawIntensities.map((v) =>
        range > 0 ? (v - minIntensity) / range : 0,
    );

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {months.map((month, index) => {
                const intensity = intensities[index];
                const isBest = hasSelection && maxScore > 0 && scores[index] === maxScore;

                const borderAlpha = hasSelection ? 0.1 + intensity * 0.9 : 0;
                const headerAlpha = hasSelection ? 0.3 + intensity * 0.7 : 1;

                return (
                    <Card
                        key={month.month}
                        id={`month-${month.month.toLowerCase()}`}
                        className={`min-h-[11.25rem] scroll-mt-32 gap-0 overflow-hidden border-border bg-card p-0 text-foreground transition-all duration-700 ease-in-out ${isBest ? "scale-[1.03] shadow-xl z-10" : ""}`}
                        style={hasSelection ? {
                            borderColor: `color-mix(in oklch, var(--color-primary) ${Math.round(borderAlpha * 100)}%, transparent)`,
                        } : undefined}
                    >
                        <CardHeader
                            className={`gap-0 px-4 py-2 transition-colors duration-700 ease-in-out ${hasSelection ? "text-primary-foreground" : "text-accent-foreground"}`}
                            style={{backgroundColor: hasSelection ? `color-mix(in oklch, var(--color-primary) ${Math.round(headerAlpha * 100)}%, transparent)` : "var(--color-accent)"}}
                        >
                            <CardTitle>
                                <h3 className={`flex items-center gap-1.5 leading-none ${isBest ? "font-bold" : "font-semibold"}`}><Calendar className="size-4" />{month.month}</h3>
                            </CardTitle>
                            {isBest && (
                                <CardAction>
                                    <Check className="size-5 transition-opacity duration-700 ease-in-out" />
                                </CardAction>
                            )}
                        </CardHeader>
                        <CardContent className="relative flex flex-1 flex-col px-4 py-3">
                            <p className="text-sm font-medium">{month.category}</p>
                            <p className="mt-1 text-xs opacity-80">{renderTextWithLinks(month.summary)}</p>
                            <div className="mt-auto min-h-13 space-y-0 pt-2">
                                {preferences.map((pref) => {
                                    const isSelected = selectedIds.has(pref.id);
                                    const sr = month.sectionRatings.find((r) => r.sectionId === pref.id);
                                    const colour = prefColourMap.get(pref.id);
                                    return (
                                        <div
                                            key={pref.id}
                                            className="grid transition-[grid-template-rows] duration-700 ease-in-out"
                                            style={{gridTemplateRows: isSelected ? "1fr" : "0fr"}}
                                        >
                                            <div className="overflow-hidden">
                                                <div className="flex items-center gap-2 pt-1.5 text-xs">
                                                    <RatingBar rating={sr?.rating ?? 0} colour={colour} />
                                                    <span className="truncate opacity-80">{prefLabelMap.get(pref.id) ?? pref.id}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {festivalsByMonth.has(month.month) && (
                                <DropdownMenu modal={false}>
                                    <DropdownMenuTrigger
                                        className="absolute end-2 bottom-2 rounded-full bg-primary/10 p-1.5 text-primary transition-colors hover:bg-primary/20"
                                        aria-label={`View festivals in ${month.month}`}
                                    >
                                        <CalendarCheck2 className="size-4" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
                                        {festivalHeading && <DropdownMenuLabel className="text-xs text-muted-foreground">{festivalHeading}</DropdownMenuLabel>}
                                        {festivalsByMonth.get(month.month)!.map((f) => (
                                            <DropdownMenuItem
                                                key={f.name}
                                                onSelect={() => {
                                                    requestAnimationFrame(() => {
                                                        document.getElementById(getFestivalAnchorId(f.month))?.scrollIntoView({behavior: "smooth"});
                                                    });
                                                }}
                                            >
                                                {f.name}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}
